import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, Select, message, Tag, Space, Popconfirm, Row, Col, Alert, Descriptions, Tooltip, Divider } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined, EyeOutlined, ExportOutlined, CopyOutlined } from '@ant-design/icons';
import api from '../../utils/api';
import dayjs from 'dayjs';
import '../../styles/third-party.css';

const { Option } = Select;

const PAYMENT_METHOD_LABELS = {
  bank_transfer: '银行卡转账',
  alipay: '支付宝',
  wechat: '微信',
  usdt: 'USDT',
  'm-pesa': 'M-Pesa',
  mobile_money: 'Mobile Money',
  united_bank: 'United bank'
};

const getPaymentAccountInfo = (acc) => {
  switch (acc.payment_method) {
    case 'mobile_money':
    case 'm-pesa':
      return acc.mpesa_phone;
    case 'bank_transfer':
      return [acc.bank_name, acc.bank_account].filter(Boolean).join(' ');
    case 'united_bank':
      return acc.bank_account;
    case 'alipay':
      return acc.alipay_account;
    case 'wechat':
      return acc.wechat_account;
    case 'usdt':
      return acc.usdt_address;
    default:
      return '';
  }
};

const formatPaymentAccountLabel = (acc) => {
  const methodLabel = PAYMENT_METHOD_LABELS[acc.payment_method] || acc.payment_method;
  const info = getPaymentAccountInfo(acc);
  if (info) {
    return `${acc.account_name} - ${info} (${methodLabel})`;
  }
  return `${acc.account_name} (${methodLabel})`;
};

const ThirdPartyApps = () => {
  const [userMap, setUserMap] = useState({});

  const copyToClipboard = (text, title = '复制成功') => {
    navigator.clipboard.writeText(text).then(() => {
      message.success(title);
    }).catch(() => {
      message.error('复制失败');
    });
  };

  const CopyBtn = ({ text, title = '复制' }) => (
    <Tooltip title={title}>
      <Button
        type="text"
        size="small"
        className="tp-copy"
        icon={<CopyOutlined />}
        onClick={() => copyToClipboard(text, title)}
      />
    </Tooltip>
  );

  const [loading, setLoading] = useState(false);
  const [apps, setApps] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [modalVisible, setModalVisible] = useState(false);
  const [detailVisible, setDetailVisible] = useState(false);
  const [testModalVisible, setTestModalVisible] = useState(false);
  const [revealedSecret, setRevealedSecret] = useState(null);
  const [testingApp, setTestingApp] = useState(null);
  const [payDomain, setPayDomain] = useState('');
  const [users, setUsers] = useState([]);
  const [regions, setRegions] = useState([]);
  const [editingApp, setEditingApp] = useState(null);
  const [appDetail, setAppDetail] = useState(null);
  const [form] = Form.useForm();
  const [testForm] = Form.useForm();
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  const [detailBoundAccounts, setDetailBoundAccounts] = useState([]);

  useEffect(() => {
    fetchApps();
    fetchUsers();
    fetchRegions();
    fetchPaymentAccounts();
  }, [page]);

  const fetchApps = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/third-party/apps', { params: { page, limit } });
      setApps(response.data.list || []);
      setTotal(response.data.pagination?.total || 0);
    } catch (error) {
      message.error('获取应用列表失败');
    }
    setLoading(false);
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/admin/users', { params: { limit: 500 } });
      const userList = response.data.list || [];
      setUsers(userList);
      const map = {};
      userList.forEach(user => { map[user.id] = user; });
      setUserMap(map);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchRegions = async () => {
    try {
      const response = await api.get('/admin/regions', { params: { isActive: 1, limit: 100 } });
      setRegions(response.data.list || []);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchPaymentAccounts = async () => {
    try {
      const response = await api.get('/admin/payment-accounts', { params: { status: 1, limit: 100 } });
      setPaymentAccounts(response.data.list || []);
    } catch (error) {
      console.error(error);
    }
  };

  const handleAdd = () => {
    setEditingApp(null);
    form.resetFields();
    form.setFieldsValue({
      status: 1,
      daily_limit: 100000,
      single_limit_min: 10,
      single_limit_max: 10000,
      fee_rate: 0,
      payment_account_ids: []
    });
    setModalVisible(true);
  };

  const handleEdit = async (record) => {
    setEditingApp(record);
    form.setFieldsValue(record);
    try {
      const response = await api.get(`/admin/third-party/apps/${record.id}/payment-accounts`);
      form.setFieldsValue({ payment_account_ids: response.data.account_ids || [] });
    } catch (error) {
      form.setFieldsValue({ payment_account_ids: [] });
    }
    setModalVisible(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const { payment_account_ids, ...appValues } = values;
      let appId;
      if (editingApp) {
        await api.put(`/admin/third-party/apps/${editingApp.id}`, appValues);
        appId = editingApp.id;
        message.success('更新成功');
      } else {
        const response = await api.post('/admin/third-party/apps', appValues);
        appId = response.data.id;
        message.success('创建成功');
        Modal.info({
          title: '应用创建成功',
          content: (
            <div>
              <p>App ID: <strong>{response.data.app_id}</strong></p>
              <p>App Secret: <strong style={{ color: 'red' }}>{response.data.app_secret}</strong></p>
              <p style={{ color: '#faad14' }}>请妥善保存 App Secret，此密钥只会显示一次！</p>
            </div>
          ),
          okText: '我已保存'
        });
      }
      if (appId) {
        await api.post(`/admin/third-party/apps/${appId}/payment-accounts`, {
          account_ids: payment_account_ids || []
        });
      }
      setModalVisible(false);
      fetchApps();
    } catch (error) {
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      }
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/admin/third-party/apps/${id}`);
      message.success('删除成功');
      fetchApps();
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleResetSecret = async (id) => {
    Modal.confirm({
      title: '确认重置密钥',
      content: '重置密钥后，旧密钥将失效。确定要重置吗？',
      okText: '确认重置',
      cancelText: '取消',
      onOk: async () => {
        try {
          const response = await api.post(`/admin/third-party/apps/${id}/reset-secret`);
          Modal.info({
            title: '密钥重置成功',
            content: (
              <div>
                <p>新的 App Secret: <strong style={{ color: 'red' }}>{response.data.app_secret}</strong></p>
                <p style={{ color: '#faad14' }}>请妥善保存，此密钥只会显示一次！</p>
              </div>
            ),
            okText: '我已保存'
          });
        } catch (error) {
          message.error('重置失败');
        }
      }
    });
  };

  const handleViewDetail = async (record) => {
    setAppDetail(record);
    setRevealedSecret(null);
    setDetailVisible(true);
    try {
      const response = await api.get(`/admin/third-party/apps/${record.id}/payment-accounts`);
      const boundIds = response.data.account_ids || [];
      const bound = paymentAccounts.filter(acc => boundIds.includes(acc.id));
      setDetailBoundAccounts(bound);
    } catch (error) {
      setDetailBoundAccounts([]);
    }
  };

  const handleRevealSecret = async () => {
    try {
      const response = await api.post(`/admin/third-party/apps/${appDetail.id}/reveal-secret`);
      setRevealedSecret(response.data.app_secret);
    } catch (error) {
      message.error('获取密钥失败');
    }
  };

  const handleTestPay = (record) => {
    setTestingApp(record);
    testForm.setFieldsValue({
      out_trade_no: `TEST${Date.now()}`,
      total_amount: 100,
      currency_code: 'CNY',
      subject: '测试商品',
      payment_method: 'm-pesa'
    });
    setTestModalVisible(true);
  };

  const handleStartTest = async () => {
    try {
      const values = await testForm.validateFields();
      let baseUrl = payDomain;
      if (!baseUrl) {
        const response = await api.get('/admin/configs/key/domain_pay');
        baseUrl = response.data.value || window.location.origin;
        setPayDomain(baseUrl);
      }
      const url = `${baseUrl}/h5/pay?app_id=${testingApp.app_id}&out_trade_no=${values.out_trade_no}&total_amount=${values.total_amount}&currency_code=${values.currency_code || 'CNY'}&subject=${encodeURIComponent(values.subject)}&payment_method=${values.payment_method}&callback_url=${encodeURIComponent(values.callback_url || '')}`;
      window.open(url, '_blank');
      setTestModalVisible(false);
    } catch (error) {
      console.error(error);
    }
  };

  const columns = [
    {
      title: 'App ID',
      dataIndex: 'app_id',
      key: 'app_id',
      width: 180,
      render: (val) => val ? <span className="tp-mono" title={val}>{val}</span> : <span className="tp-empty">-</span>
    },
    {
      title: '应用名称',
      dataIndex: 'app_name',
      key: 'app_name',
      width: 160,
      render: (val) => val ? <span className="tp-mono" title={val}>{val}</span> : <span className="tp-empty">-</span>
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 88,
      render: (status) => (
        <span className={`tp-tag ${status === 1 ? 'tp-tag--paid' : 'tp-tag--failed'}`}>
          {status === 1 ? '启用' : '禁用'}
        </span>
      )
    },
    {
      title: '日限额',
      dataIndex: 'daily_limit',
      key: 'daily_limit',
      width: 120,
      render: (val) => <span className="tp-num">{Number(val || 0).toLocaleString('en-US')}</span>
    },
    {
      title: '单笔限额',
      key: 'limit',
      width: 160,
      render: (_, record) => (
        <span className="tp-mono">{record.single_limit_min} - {record.single_limit_max}</span>
      )
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (time) => time ? <span className="tp-time">{dayjs(time).format('YYYY-MM-DD HH:mm')}</span> : <span className="tp-empty">-</span>
    },
    {
      title: '操作',
      key: 'action',
      width: 280,
      render: (_, record) => (
        <div className="tp-actions">
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => handleViewDetail(record)}>
            详情
          </Button>
          <Button type="link" size="small" icon={<ExportOutlined />} onClick={() => handleTestPay(record)}>
            测试
          </Button>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            编辑
          </Button>
          <Button type="link" size="small" icon={<ReloadOutlined />} onClick={() => handleResetSecret(record.id)}>
            重置密钥
          </Button>
          <Popconfirm title="确认删除?" onConfirm={() => handleDelete(record.id)}>
            <Button type="link" size="small" className="tp-link--danger" icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </div>
      )
    }
  ];

  return (
    <div className="tp-page">
      <div className="tp-card tp-toolbar">
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          创建应用
        </Button>
      </div>
      <div className="tp-card tp-table-card">
        <Table
          columns={columns}
          dataSource={apps}
          rowKey="id"
          loading={loading}
          size="middle"
          scroll={{ x: 1160 }}
          pagination={{
            current: page,
            pageSize: limit,
            total: total,
            onChange: setPage,
            showSizeChanger: false,
            showTotal: (t) => `共 ${t} 条`
          }}
        />
      </div>

      <Modal
        className="tp-modal"
        title={editingApp ? '编辑应用' : '创建应用'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="app_name" label="应用名称" rules={[{ required: true, message: '请输入应用名称' }]}>
            <Input placeholder="请输入应用名称" />
          </Form.Item>
          <Form.Item name="app_desc" label="应用描述">
            <Input.TextArea rows={2} placeholder="请输入应用描述" />
          </Form.Item>
          <Form.Item name="user_id" label="关联用户（用于接收结算金额）">
            <Select placeholder="选择绑定的用户账户（用于接收结算金额）" allowClear showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {users.map(user => (
                <Option key={user.id} value={user.id}>
                  {user.real_name || user.phone || user.id} {user.phone ? `(${user.phone})` : ''}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="callback_url" label="支付回调地址">
            <Input placeholder="用户支付完成后跳转的页面" />
          </Form.Item>
          <Form.Item name="webhook_url" label="Webhook通知地址">
            <Input placeholder="支付成功后异步通知的地址" />
          </Form.Item>
          <Form.Item name="ip_whitelist" label="IP白名单">
            <Input placeholder="多个IP用逗号分隔，留空表示不限制" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="daily_limit" label="日限额">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="fee_rate" label="手续费率">
                <InputNumber min={0} max={1} step={0.001} style={{ width: '100%' }} placeholder="如 0.001 表示 0.1%" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="single_limit_min" label="单笔最低限额">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="single_limit_max" label="单笔最高限额">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Divider orientation="left">代付设置</Divider>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="withdraw_fee_rate" label="代付手续费率">
                <InputNumber min={0} max={1} step={0.001} style={{ width: '100%' }} placeholder="如 0.001 表示 0.1%" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="withdraw_callback_url" label="代付回调地址">
                <Input placeholder="代付状态变更通知地址" />
              </Form.Item>
            </Col>
          </Row>
          <Divider orientation="left">收款账户绑定</Divider>
          <Form.Item name="payment_account_ids" label="绑定收款账户" extra="不绑定则使用全部可用账户">
            <Select mode="multiple" placeholder="选择绑定的收款账户（留空=全部）" allowClear
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {paymentAccounts.map(acc => (
                <Option key={acc.id} value={acc.id}>
                  {formatPaymentAccountLabel(acc)}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="status" label="状态" initialValue={1}>
            <Select>
              <Option value={1}>启用</Option>
              <Option value={0}>禁用</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        className="tp-modal"
        title="应用详情"
        open={detailVisible}
        onCancel={() => { setDetailVisible(false); setRevealedSecret(null); }}
        footer={[
          <Button key="test" icon={<ExportOutlined />} onClick={() => { setDetailVisible(false); handleTestPay(appDetail); }}>
            测试支付
          </Button>,
          <Button key="reset" icon={<ReloadOutlined />} onClick={() => { setDetailVisible(false); handleResetSecret(appDetail.id); }}>
            重置密钥
          </Button>,
          <Button key="edit" type="primary" icon={<EditOutlined />} onClick={() => { setDetailVisible(false); handleEdit(appDetail); }}>
            编辑
          </Button>
        ]}
        width={700}
      >
        {appDetail && (
          <div>
            <Alert 
              message={appDetail.app_name} 
              type={appDetail.status === 1 ? 'success' : 'error'}
              showIcon
              icon={appDetail.status === 1 ? <EyeOutlined /> : <ExportOutlined />}
              style={{ marginBottom: 16 }}
            />
            <Descriptions column={2} bordered size="small">
              <Descriptions.Item label="App ID" span={2}>
                <Space>
                  <span className="tp-mono">{appDetail.app_id}</span>
                  <CopyBtn text={appDetail.app_id} title="复制App ID" />
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="状态">
                <span className={`tp-tag ${appDetail.status === 1 ? 'tp-tag--paid' : 'tp-tag--failed'}`}>
                  {appDetail.status === 1 ? '启用' : '禁用'}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="手续费率">
                {appDetail.fee_rate > 0 ? `${(appDetail.fee_rate * 100).toFixed(2)}%` : '免费'}
              </Descriptions.Item>
              <Descriptions.Item label="代付手续费率">
                {appDetail.withdraw_fee_rate > 0 ? `${(appDetail.withdraw_fee_rate * 100).toFixed(2)}%` : '免费'}
              </Descriptions.Item>
              <Descriptions.Item label="代付回调地址" span={2}>
                {appDetail.withdraw_callback_url || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="日限额">
                {appDetail.daily_limit?.toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label="单笔限额">
                {appDetail.single_limit_min} - {appDetail.single_limit_max}
              </Descriptions.Item>
              <Descriptions.Item label="关联用户" span={2}>
                {appDetail.user_id ? (
                  <Space>
                    <span>{userMap[appDetail.user_id]?.real_name || userMap[appDetail.user_id]?.phone || appDetail.user_id}</span>
                    <span> 【 {userMap[appDetail.user_id]?.phone} 】</span>
                    <Tag>ID: {appDetail.user_id}</Tag>
                  </Space>
                ) : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="绑定收款账户" span={2}>
                {detailBoundAccounts.length > 0 ? (
                  <Space wrap>
                    {detailBoundAccounts.map(acc => (
                      <Tag key={acc.id}>{formatPaymentAccountLabel(acc)}</Tag>
                    ))}
                  </Space>
                ) : <span className="tp-empty">未绑定（使用全部可用账户）</span>}
              </Descriptions.Item>
              <Descriptions.Item label="回调地址" span={2}>
                {appDetail.callback_url ? (
                  <Space>
                    <span style={{ wordBreak: 'break-all' }}>{appDetail.callback_url}</span>
                    <CopyBtn text={appDetail.callback_url} title="复制回调地址" />
                  </Space>
                ) : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Webhook地址" span={2}>
                {appDetail.webhook_url ? (
                  <Space>
                    <span style={{ wordBreak: 'break-all' }}>{appDetail.webhook_url}</span>
                    <CopyBtn text={appDetail.webhook_url} title="复制Webhook地址" />
                  </Space>
                ) : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="IP白名单">
                {appDetail.ip_whitelist || '不限制'}
              </Descriptions.Item>
              <Descriptions.Item label="创建时间">
                {appDetail.created_at ? dayjs(appDetail.created_at).format('YYYY-MM-DD HH:mm') : '-'}
              </Descriptions.Item>
            </Descriptions>
            <div className="tp-secret">
              <div className="tp-secret__label">App Secret</div>
              <div className="tp-secret__row">
                <span className="tp-mono">
                  {revealedSecret || '••••••••••••••••••••••••••••••••'}
                </span>
                {!revealedSecret && (
                  <Button size="small" type="link" onClick={handleRevealSecret}>查看密钥</Button>
                )}
                {revealedSecret && <CopyBtn text={revealedSecret} title="复制密钥" />}
              </div>
              {!revealedSecret && <p className="tp-muted" style={{ marginTop: 8, marginBottom: 0 }}>点击查看完整密钥</p>}
            </div>
            {appDetail.app_desc && (
              <div className="tp-section">
                <div className="tp-section__title">应用描述</div>
                <div className="tp-desc">{appDetail.app_desc}</div>
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal
        className="tp-modal"
        title="测试H5支付"
        open={testModalVisible}
        onOk={handleStartTest}
        onCancel={() => setTestModalVisible(false)}
        okText="开始测试"
        width={500}
      >
        {testingApp && (
          <div>
            <Alert message={`测试应用: ${testingApp.app_name}`} type="info" showIcon style={{ marginBottom: 16 }} />
            <Form form={testForm} layout="vertical">
              <Form.Item name="out_trade_no" label="商户订单号" rules={[{ required: true }]}>
                <Input placeholder="请输入商户订单号" />
              </Form.Item>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="total_amount" label="金额" rules={[{ required: true }]}>
                    <InputNumber min={1} style={{ width: '100%' }} placeholder="请输入金额" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="currency_code" label="货币" rules={[{ required: true }]}>
                    <Select placeholder="请选择货币" showSearch optionFilterProp="children">
                      {regions.map(region => (
                        <Option key={region.currency_code} value={region.currency_code}>
                          {region.currency_code} - {region.currency_name}
                        </Option>
                      ))}
                      {/* <Option value="CNY">CNY - 人民币</Option>
                      <Option value="KES">KES - 肯尼亚先令</Option> */}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name="subject" label="商品标题" rules={[{ required: true }]}>
                <Input placeholder="请输入商品标题" />
              </Form.Item>
              <Form.Item name="payment_method" label="支付方式" rules={[{ required: true }]}>
                <Select placeholder="请选择支付方式">
                  <Option value="bank_transfer">银行转账</Option>
                  <Option value="alipay">支付宝</Option>
                  <Option value="wechat">微信支付</Option>
                  <Option value="usdt">USDT</Option>
                  <Option value="m-pesa">M-Pesa</Option>
                  <Option value="mobile_money">mobile_money</Option>
                </Select>
              </Form.Item>
              <Form.Item name="callback_url" label="回调地址(可选)">
                <Input placeholder="支付完成后跳转的页面" />
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ThirdPartyApps;
